import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import Sidebar from "@/pages/sidebar/sidebar";
import ClientMainHeader from "@/pages/header";
import { StyledInput } from "@/pages/utils/styled-input";
import { StyledDrawer } from "@/pages/utils/styled-drawer";
import { updateSettings, uploadLogo } from "./actions";
import { MainWrapper, OptionLabel } from "@/pages/styled";
import { BtnSection, FormField, FormLabel, ProfileSection } from "./styled";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { useResetPassword } from "@/hooks/auth/use-reset-password";
import { useAuth } from "@/providers/auth";
import { Button } from "@/components/ui/button-tw";
import {
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Plus,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { LimitedAccountWarning } from "@/components/limited-account-warning";
import { MerchantProcessingBanner } from "@/components/merchant-processing-banner";
import { useSettings } from "@/hooks/setting/use-settings";
import { usePlatformUsers } from "@/hooks/tenancy/use-platform-users";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAdminInvite } from "@/hooks/tenancy/use-admin-invite";
import { useTenancy } from "@/hooks/tenancy/use-tenancy";
import { useDeleteAdmin } from "@/hooks/tenancy/use-delete-admin";
import { useProfile } from "@/hooks/profile/use-profile";
import { InputBase } from "@/components/ui/input-base";
import { formatPhoneUS } from "@/utils/functions";
import { FileUploader } from "react-drag-drop-files";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";
import { usePlatformData } from "@/hooks/platform/use-platform-data";
import { useAuthUserInvite } from "@/hooks/tenancy/use-auth-user-invite";
import { useAuthUsers } from "@/hooks/tenancy/use-auth-users";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteAuthUser } from "@/hooks/tenancy/use-delete-auth-user";
import { useDemoteAuthUser } from "@/hooks/tenancy/use-demote-auth-user";
import { capitalize } from "@mui/material";

const Settings = () => {
  const [shortCut, setShortCut] = useState("");
  const [drawer, setDrawer] = useState({
    top: false,
  });
  const [currentWidth, setCurrentWidth] = useState(0);

  return (
    <div className="h-100">
      <Sidebar currentWidth={currentWidth} setCurrentWidth={setCurrentWidth} />
      <>
        <LimitedAccountWarning />
        <MerchantProcessingBanner />

        <MainWrapper>
          <ClientMainHeader
            title="Settings"
            subTitle="View your Information, Manage Users, Custom Branding, and Statements"
            shortCut={shortCut}
            setShortCut={setShortCut}
          />
          <div className="flex flex-col gap-8 max-w-4xl mt-12 mb-32">
            <PersonalInfoSection />
            <BusinessInfoSection />
            <BrandingSection />
            <AdminSection />
            <SubscriptionSection />
            <SecuritySection />
          </div>
        </MainWrapper>
      </>
      <StyledDrawer
        state={drawer}
        setState={setDrawer}
        text="Your changes has been saved."
      />
    </div>
  );
};

export default Settings;

function PersonalInfoSection() {
  const { data: profile } = useProfile();

  return (
    <section aria-labelledby="business-information-heading">
      <form>
        <div className="shadow sm:overflow-hidden sm:rounded-md">
          <div className="bg-white px-4 py-6 sm:p-6">
            <div>
              <h2 className="text-lg font-medium leading-6 text-gray-900">
                Personal information
              </h2>
              <div className="text-sm text-gray-500 mt-1 flex items-center">
                <p>
                  To update your personal information, please{" "}
                  <a
                    href="https://pocketbookhelp.zendesk.com/hc/en-us"
                    target="_blank"
                    className=" font-medium text-yellow-600 hover:border-b border-yellow-600"
                  >
                    contact us
                  </a>
                </p>
              </div>
            </div>

            <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  First name
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <div className="text-gray-900">{profile?.first_name}</div>
                  {/* <button
                    type="button"
                    className="font-semibold text-indigo-600/65 cursor-not-allowed"
                  >
                    Update
                  </button> */}
                </dd>
              </div>
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  Last name
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <div className="text-gray-900">{profile?.last_name}</div>
                  {/* <button
                    type="button"
                    className="font-semibold text-indigo-600/65 cursor-not-allowed"
                  >
                    Update
                  </button> */}
                </dd>
              </div>
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  Email address
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <div className="text-gray-900">{profile?.business_email}</div>
                  {/* <button
                    type="button"
                    className="font-semibold text-indigo-600/65 cursor-not-allowed"
                  >
                    Update
                  </button> */}
                </dd>
              </div>
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  Phone
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <div className="text-gray-900">
                    {formatPhoneUS(profile?.business_phone)}
                  </div>
                  {/* <button
                    type="button"
                    className="font-semibold text-indigo-600/65 cursor-not-allowed"
                  >
                    Update
                  </button> */}
                </dd>
              </div>
            </dl>
          </div>
          <div className="bg-white px-4 py-3 text-right sm:px-6"></div>
        </div>
      </form>
    </section>
  );
}

function BusinessInfoSection() {
  const { data: profile } = useProfile();

  return (
    <section aria-labelledby="business-information-heading">
      <form>
        <div className="shadow sm:overflow-hidden sm:rounded-md">
          <div className="bg-white px-4 py-6 sm:p-6">
            <div>
              <h2 className="text-lg font-medium leading-6 text-gray-900">
                Business information
              </h2>
              <div className="text-sm text-gray-500 mt-1">
                <p>
                  To update your business information, please{" "}
                  <a
                    href="https://pocketbookhelp.zendesk.com/hc/en-us"
                    target="_blank"
                    className=" font-medium text-yellow-600 hover:border-b border-yellow-600"
                  >
                    contact us
                  </a>
                </p>
              </div>
            </div>

            <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  Business name
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <div className="text-gray-900">{profile?.business_name}</div>
                  {/* <button
                    type="button"
                    className="font-semibold text-indigo-600/65 cursor-not-allowed"
                  >
                    Update
                  </button> */}
                </dd>
              </div>
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  Business DBA
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <div className="text-gray-900">{profile?.business_dba}</div>
                  {/* <button
                    type="button"
                    className="font-semibold text-indigo-600/65 cursor-not-allowed"
                  >
                    Update
                  </button> */}
                </dd>
              </div>
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  Mailing address
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <div>
                    <div className="text-gray-900">
                      {profile?.physical_address?.street_line_1}
                    </div>
                    <div className="text-gray-900">
                      {`${profile?.physical_address?.city}, ${profile?.physical_address?.state} ${profile?.physical_address?.postal_code}`}
                    </div>
                    <div className="text-gray-900">
                      {profile?.physical_address?.country}
                    </div>
                  </div>
                  {/* <button
                    type="button"
                    className="font-semibold text-indigo-600/65 cursor-not-allowed"
                  >
                    Update
                  </button> */}
                </dd>
              </div>
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  Timezone
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <div className="text-gray-900">{profile?.time_zone}</div>
                  {/* <button
                    type="button"
                    className="font-semibold text-indigo-600/65 cursor-not-allowed"
                  >
                    Update
                  </button> */}
                </dd>
              </div>
            </dl>
          </div>
          <div className="bg-white px-4 py-3 text-right sm:px-6"></div>
        </div>
      </form>
    </section>
  );
}

function BrandingSection() {
  const client = useQueryClient();
  const dispatch = useDispatch();
  const { cognitoId } = useAuth();
  const { data: settings } = useSettings();
  const { data: platform } = usePlatformData();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [newLogo, setNewLogo] = useState(null);

  const handleChangeLogo = async (file) => {
    setNewLogo(file);
  };

  const handleUploadLogo = async () => {
    if (newLogo) {
      try {
        setIsUploading(true);

        try {
          const resp = await uploadLogo({
            userId: cognitoId,
            file: newLogo,
          });

          const payload = {
            logo: resp.url,
          };

          await updateSettings({ userId: cognitoId, payload });
          toast.success("Logo uploaded successfully");
          setIsUpdating(false);
        } catch (err) {
          toast.error(
            "An error occurred while uploading the logo. Please try again.",
          );
        }
      } finally {
        setIsUploading(false);
        client.invalidateQueries(["settings"]);
        // await getSettings(cognitoId).then((settings) => {
        //   dispatch({
        //     type: actionTypes.SETTING_DATA,
        //     payload: {
        //       ...settings,
        //       logo: settings.logo ? `${settings.logo}?${Date.now()}` : "",
        //     },
        //   });
        // });
      }
    }
  };

  return (
    <section aria-labelledby="business-information-heading">
      <form>
        <div className="shadow sm:overflow-hidden sm:rounded-md">
          <div className="bg-white px-4 py-6 sm:p-6">
            <div>
              <h2 className="text-lg font-medium leading-6 text-gray-900">
                Custom branding
              </h2>
              <div className="text-sm text-gray-500 mt-1">
                Updates to branding will show on the invoices sent to customers
              </div>
            </div>

            <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  <div>Logo</div>
                  <div className="text-sm font-normal text-gray-500">
                    For optimal performance and the best user experience, please
                    upload a PNG image that contrasts well against the branding
                    color.
                  </div>
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  {isUpdating || !settings?.logo ? (
                    <div className="w-full">
                      <FileUploader
                        multiple={false}
                        handleChange={handleChangeLogo}
                        name="file"
                        types={["PNG", "JPG", "JPEG", "SVG"]}
                        children={
                          <div className="flex items-center justify-center w-full">
                            <label
                              htmlFor="dropzone-file"
                              className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 "
                            >
                              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                <svg
                                  className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                                  aria-hidden="true"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 20 16"
                                >
                                  <path
                                    stroke="currentColor"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                                  />
                                </svg>
                                {newLogo ? (
                                  <p className="mb-2 text-lg text-gray-500 dark:text-gray-400">
                                    {newLogo.name}
                                  </p>
                                ) : (
                                  <>
                                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                      <span className="font-semibold">
                                        Click to upload
                                      </span>{" "}
                                      or drag and drop
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      PNG or JPG (MAX. 800x400px)
                                    </p>
                                  </>
                                )}
                              </div>
                              <input
                                id="dropzone-file"
                                type="file"
                                className="hidden"
                              />
                            </label>
                          </div>
                        }
                      />
                    </div>
                  ) : (
                    <img
                      src={`https://cdn.pocketbook.tech/${settings?.logo}`}
                      className="h-32 w-auto max-w-xs object-contain"
                    />
                  )}
                  {!isUpdating && settings?.logo && (
                    <button
                      type="button"
                      className="font-semibold text-indigo-700 hover:text-indigo-600"
                      onClick={() => setIsUpdating(true)}
                    >
                      Update
                    </button>
                  )}
                </dd>
              </div>
            </dl>
          </div>
          <div className="bg-white px-4 py-3 text-right sm:px-6">
            {(isUpdating || !settings?.logo) && (
              <div className="flex justify-end gap-x-3">
                <Button
                  disabled={isUploading}
                  variant="outline"
                  onClick={() => {
                    setNewLogo(null);
                    setIsUpdating(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUploadLogo}
                  disabled={isUploading || !newLogo}
                >
                  Upload{" "}
                  {isUploading ? (
                    <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                  ) : (
                    <UploadCloud className="w-4 h-4 ml-2 " />
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </form>
    </section>
  );
}

function AdminSection() {
  const queryClient = useQueryClient();
  const { data: tenancy } = useTenancy();
  const { data: admins } = usePlatformUsers({ userType: "admin" });
  const { mutate: deleteAdmin, isLoading: deleting } = useDeleteAdmin();
  const { data: authUsers } = useAuthUsers();
  const { user } = useAuth();
  const userRole = user.cognito_user_info.UserRole;

  const adminUsers = useMemo(() => {
    return admins?.filter(
      (admin) => !authUsers?.some((u) => u.user_id === admin.user_id),
    );
  }, [authUsers, admins]);

  const handleDelete = ({ email_address, user_id }) => {
    deleteAdmin(
      { email: email_address, userId: user_id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["tenancy"]);
          toast.success("Admin user deleted successfully!");
        },
        onError: () => {
          toast.error(
            "Something went wrong while deleting admin user. Please try again.",
          );
        },
      },
    );
  };

  return (
    <section aria-labelledby="administrators-heading">
      <form>
        <div className="shadow sm:overflow-hidden sm:rounded-md">
          <div className="bg-white py-6 ">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ">
              <h2 className="text-lg font-semibold leading-6 text-gray-900 mb-4 border-b pb-4">
                Organization Members
              </h2>
              <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Admin
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Admins have access to send invoices, manage integrations and
                    contacts but are not allowed to move money or pay bills. You
                    can add up to 10 administrators to your platform.
                  </p>
                </div>
                <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                  <AddAdminModal />
                </div>
              </div>
            </div>
            <div className="mt-8 flow-root overflow-hidden">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <table className="w-full text-left">
                  <thead className="bg-white">
                    <tr>
                      <th
                        scope="col"
                        className="relative isolate py-3.5 pr-3 text-left text-sm font-semibold text-gray-900"
                      >
                        Name
                        <div className="absolute inset-y-0 right-full -z-10 w-screen border-b border-b-gray-200" />
                        <div className="absolute inset-y-0 left-0 -z-10 w-screen border-b border-b-gray-200" />
                      </th>
                      <th
                        scope="col"
                        className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 md:table-cell"
                      >
                        Email
                      </th>
                      <th
                        scope="col"
                        className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 md:table-cell"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 md:table-cell"
                      >
                        Role
                      </th>
                      {userRole === "owner" &&
                        tenancy?.feature_flags?.auth_user_create && (
                          <th scope="col" className="relative py-3.5 pl-3">
                            <span className="sr-only">
                              Elevate to auth user
                            </span>
                          </th>
                        )}
                      <th scope="col" className="relative py-3.5 pl-3">
                        <span className="sr-only">Remove</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers?.map((user) => {
                      if (user.user_type === "owner") return undefined;

                      return (
                        <tr key={user.email_address}>
                          <td className="relative py-4 pr-3 text-sm font-medium text-gray-900 truncate">
                            {`${user.first_name} ${user.last_name}`}
                            <div className="absolute bottom-0 right-full h-px w-screen bg-gray-100" />
                            <div className="absolute bottom-0 left-0 h-px w-screen bg-gray-100" />
                          </td>
                          <td className="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                            {user.email_address}
                          </td>
                          <td className="hidden px-3 py-4 text-sm text-gray-500 md:table-cell">
                            {capitalize(user.record_status)}
                          </td>
                          <td className="hidden px-3 py-4 text-sm text-gray-500 md:table-cell">
                            {capitalize(user.user_type)}
                          </td>
                          {userRole === "owner" &&
                            tenancy?.feature_flags?.auth_user_create && (
                              <td className="relative py-4 pl-3 text-right text-sm font-medium">
                                <AuthUserButton
                                  user={user}
                                  disabled={deleting || authUsers?.length >= 4}
                                />
                              </td>
                            )}
                          <td className="relative py-4 pl-3 text-right text-sm font-medium">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="hover:bg-red-50"
                                >
                                  <Trash2 className="w-5 h-5 text-red-600" />
                                  <span className="sr-only">
                                    , {`${user.first_name} ${user.last_name}`}
                                  </span>
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Are you absolutely sure?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {`Are you sure you want to delete ${user.first_name} ${user.last_name}? This action cannot be undone. Admins cannot be added back once they have been removed.`}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel disabled={deleting}>
                                    Cancel
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    disabled={deleting}
                                    onClick={() => handleDelete(user)}
                                    className="bg-red-600 hover:bg-red-700 disabled:bg-red-100"
                                  >
                                    Continue
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="bg-white py-6 ">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ">
              <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">
                    Authorized Users
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Authorized users can move money and pay bills. Only give
                    this role to someone authorized to make financial decisions
                    for your organization. You can add up to 4 authorized users
                    to your platform.
                  </p>
                </div>
                {userRole === "owner" &&
                  tenancy?.feature_flags?.auth_user_create && (
                    <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                      <AddAuthUserModal disabled={authUsers?.length >= 4} />
                    </div>
                  )}
              </div>
            </div>
            <div className="mt-8 flow-root overflow-hidden">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <table className="w-full text-left">
                  <thead className="bg-white">
                    <tr>
                      <th
                        scope="col"
                        className="relative isolate py-3.5 pr-3 text-left text-sm font-semibold text-gray-900"
                      >
                        Name
                        <div className="absolute inset-y-0 right-full -z-10 w-screen border-b border-b-gray-200" />
                        <div className="absolute inset-y-0 left-0 -z-10 w-screen border-b border-b-gray-200" />
                      </th>
                      <th
                        scope="col"
                        className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell"
                      >
                        Email
                      </th>
                      <th
                        scope="col"
                        className=" px-3 py-3.5 text-left text-sm font-semibold text-gray-900 table-cell"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className=" px-3 py-3.5 text-left text-sm font-semibold text-gray-900 table-cell"
                      >
                        Role
                      </th>
                      {userRole === "owner" &&
                        tenancy?.feature_flags?.auth_user_edit && (
                          <th scope="col" className="relative py-3.5 pl-3">
                            <span className="sr-only">Actions</span>
                          </th>
                        )}
                    </tr>
                  </thead>
                  <tbody>
                    {authUsers?.map((user) => {
                      if (user.user_type === "owner") return undefined;

                      return (
                        <AuthUserTableRow user={user} key={user?.user_id} />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </form>
    </section>
  );
}

function AuthUserTableRow({ user }) {
  const { data: tenancy } = useTenancy();
  const [deleteAuthUserOpen, setDeleteAuthUserOpen] = useState(false);
  const [downgradeAuthUserOpen, setDowngradeAuthUserOpen] = useState(false);
  const { user: auth } = useAuth();
  const userRole = auth.cognito_user_info.UserRole;

  const roleLabel = user?.status === "approved" ? "Auth User" : "Admin";
  const status =
    user?.status === "rejected"
      ? "Not Approved"
      : user?.status === "approved"
      ? "Approved"
      : "Pending";

  return (
    <tr key={user.email_address}>
      <td className="relative py-4 pr-3 text-sm font-medium text-gray-900">
        {`${user.first_name} ${user.last_name}`}
        <div className="absolute bottom-0 right-full h-px w-screen bg-gray-100" />
        <div className="absolute bottom-0 left-0 h-px w-screen bg-gray-100" />
      </td>
      <td className="hidden px-3 py-4 text-sm text-gray-500 lg:table-cell">
        {user.email}
      </td>
      <td className=" px-3 py-4 text-sm text-gray-500 table-cell">{status}</td>
      <td className=" px-3 py-4 text-sm text-gray-500 table-cell">
        {roleLabel}
      </td>
      {userRole === "owner" && tenancy?.feature_flags?.auth_user_edit && (
        <td>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="flex justify-end">
                <Button
                  size="icon"
                  variant="ghost"
                  className="shadow-none"
                  aria-label="Edit item"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-ellipsis"
                  >
                    <circle cx="12" cy="12" r="1" />
                    <circle cx="19" cy="12" r="1" />
                    <circle cx="5" cy="12" r="1" />
                  </svg>
                </Button>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setDowngradeAuthUserOpen(true)}>
                <span>Demote</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />

              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => setDeleteAuthUserOpen(true)}
              >
                <span>Delete</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </td>
      )}
      <DowngradeAuthUserAlert
        open={downgradeAuthUserOpen}
        handleChange={setDowngradeAuthUserOpen}
        user={user}
      />
      <DeleteAuthUserAlert
        open={deleteAuthUserOpen}
        handleChange={setDeleteAuthUserOpen}
        user={user}
      />
    </tr>
  );
}

function DowngradeAuthUserAlert({ open, handleChange, user }) {
  const queryClient = useQueryClient();
  const { mutate: demoteAuthUser, isLoading: demotingAuthUser } =
    useDemoteAuthUser();

  return (
    <AlertDialog open={open} onOpenChange={handleChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
          <AlertDialogDescription>
            {`By clicking "continue" ${user.first_name} ${user.last_name} will be downgraded and will have only admin permissions.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={demotingAuthUser}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={demotingAuthUser}
            onClick={() => {
              demoteAuthUser(
                { userId: user?.user_id },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries(["tenancy"]);
                    toast.success("Authorized user downgraded successfully!");
                  },
                  onError: () => {
                    toast.error(
                      "Something went wrong while downgrading authorized user. Please try again.",
                    );
                  },
                },
              );
            }}
          >
            Continue
            {demotingAuthUser && (
              <Loader2 className="w-4 h-4 ml-1 animate-spin" />
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DeleteAuthUserAlert({ open, handleChange, user }) {
  const queryClient = useQueryClient();
  const { mutate: deleteAuthUser, isLoading: deletingAuthUser } =
    useDeleteAuthUser();

  return (
    <AlertDialog open={open} onOpenChange={handleChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
          <AlertDialogDescription>
            {`Are you sure you want to delete ${user.first_name} ${user.last_name}? This action cannot be undone. Authorized users cannot be added back once they have been removed.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deletingAuthUser}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={deletingAuthUser}
            onClick={() => {
              deleteAuthUser(
                { userId: user?.user_id },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries(["tenancy"]);
                    toast.success("Authorized user deleted successfully!");
                  },
                  onError: () => {
                    toast.error(
                      "Something went wrong while deleting authorized user. Please try again.",
                    );
                  },
                },
              );
            }}
            className="bg-red-600 hover:bg-red-700 disabled:bg-red-100"
          >
            Continue
            {deletingAuthUser && (
              <Loader2 className="w-4 h-4 ml-1 animate-spin" />
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AuthUserButton({ user, disabled = false }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const { mutate: sendInvite, isLoading: inviting } = useAuthUserInvite();

  return (
    <Dialog open={open} onOpenChange={(open) => !inviting && setOpen(open)}>
      <DialogTrigger asChild>
        <Button
          variant="secondary"
          disabled={user.record_status !== "active" || disabled}
        >
          {" "}
          <Plus className="w-4 h-4 mr-2" />
          Auth User
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {`You're Upgrading ${user.first_name} ${user.last_name}`}
          </DialogTitle>
          <div className="text-sm text-muted-foreground">
            Empower a trusted team member with top-level access to your account.
            An Authorized User can do everything an Admin can—plus manage
            high-level settings to keep your account running smoothly.{" "}
            <div className="mt-3 mb-1 font-medium text-base text-slate-900">
              Authorized users can:
            </div>
            <ul>
              <li className="flex items-center">
                <ArrowRight className="w-4 h-4 mr-2" />
                Add or Remove Bank Accounts
              </li>
              <li className="flex items-center">
                <ArrowRight className="w-4 h-4 mr-2" />
                Move Money
              </li>
              <li className="flex items-center">
                <ArrowRight className="w-4 h-4 mr-2" />
                Manage Platform Settings
              </li>
              <li className="flex items-center">
                <ArrowRight className="w-4 h-4 mr-2" />
                Oversee Admin Users
              </li>
              <li className="flex items-center">
                <ArrowRight className="w-4 h-4 mr-2" />
                Plus All Standard Admin Functions
              </li>
            </ul>
          </div>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              sendInvite(
                { user_id: user.user_id },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries(["tenancy"]);
                    setOpen(false);
                    toast.success(
                      `Invite email to  ${user.first_name} ${user.last_name} sent successfully!`,
                      {
                        autoClose: 10000,
                        hideProgressBar: false,
                      },
                    );
                  },
                  onError: (error) => {
                    if (error.response.status === 409) {
                      toast.error(
                        error?.response?.data?.detail ||
                          "The maximum number of authorized users (4) has been reached.",
                        {
                          autoClose: 15000,
                          hideProgressBar: false,
                        },
                      );
                    } else {
                      toast.error(
                        "Something went wrong sending the invite. Please try again.",
                        {
                          autoClose: 10000,
                          hideProgressBar: false,
                        },
                      );
                    }
                  },
                },
              );
            }}
            disabled={inviting}
          >
            Continue
            {inviting && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubscriptionSection() {
  const { data: profile } = useProfile();
  const history = useHistory();

  return (
    <section aria-labelledby="business-information-heading">
      <form>
        <div className="shadow sm:overflow-hidden sm:rounded-md">
          <div className="bg-white px-4 py-6 sm:p-6">
            <div>
              <h2 className="text-lg font-medium leading-6 text-gray-900">
                Account Information
              </h2>
            </div>

            <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  Account / Routing #
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <div className="text-gray-900">
                    For your account / routing #, please{" "}
                    <a
                      href="https://pocketbookhelp.zendesk.com/hc/en-us"
                      target="_blank"
                      className=" font-medium text-yellow-600 hover:border-b border-yellow-600"
                    >
                      contact us
                    </a>
                  </div>
                  {/* <button
                    type="button"
                    className="font-semibold text-indigo-600/65 cursor-not-allowed"
                  >
                    Update
                  </button> */}
                </dd>
              </div>
              <div className="pt-6 sm:flex">
                <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                  Statements
                </dt>
                <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                  <Button
                    type="button"
                    onClick={() => history.push("/account-statement")}
                  >
                    View all past statements
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </dd>
              </div>
            </dl>
          </div>
          <div className="bg-white px-4 py-3 text-right sm:px-6"></div>
        </div>
      </form>
    </section>
  );
}

const makeSteps = (password) => [
  { id: "A-Z", name: "Uppercase", completed: /[A-Z]+/.test(password) },
  { id: "a-z", name: "Lowercase", completed: /[a-z]+/.test(password) },
  { id: "0-9", name: "Number", completed: /[0-9]+/.test(password) },
  {
    id: "/*+&...",
    name: "Special",
    completed: /[$&+,:;=?@#|'<>.^*()%!-]+/.test(password),
  },
  { id: "+12 char", name: "Chars", completed: password.length >= 12 },
];

function PasswordValidation({ password }) {
  if (typeof password !== "string") {
    throw new Error(
      "[PASSWORD VALIDATION COMPONENT]::Password must be a string value.",
    );
  }

  const steps = makeSteps(password);

  return (
    <nav aria-label="Progress">
      <ol className="space-y-4 md:flex md:space-x-4 md:space-y-0">
        {steps.map((step) => (
          <li key={step.name} className="md:flex-1">
            {step.completed ? (
              <span className="group flex flex-col border-l-4 border-emerald-600 py-2 pl-4 hover:border-emerald-800 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4">
                <span className="text-sm font-medium text-emerald-600 group-hover:text-emerald-800">
                  {step.id}
                </span>
              </span>
            ) : (
              <span className="group flex flex-col border-l-4 border-gray-200 py-2 pl-4 hover:border-gray-300 md:border-l-0 md:border-t-4 md:pb-0 md:pl-0 md:pt-4">
                <span className="text-sm font-medium text-gray-500 group-hover:text-gray-700">
                  {step.id}
                </span>
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

const passwordSchema = yup
  .object({
    currentPassword: yup
      .string()
      .required("Current password is a required field"),
    newPassword: yup
      .string()
      .required("New Password is required")
      .min(12, "Password length should be at least 12 characters")
      .matches(/\d/, "Password must contain at least one number")
      .matches(
        /[^A-Za-z0-9\s]/,
        "Password must contain at least one special character",
      )
      .matches(/[A-Z]/, "Passwors must contain at least one uppercase letter"),
    confirmNewPassword: yup
      .string()
      .required("Confirm Password is required")
      .oneOf([yup.ref("newPassword")], "Passwords do not match"),
  })
  .required();

function SecuritySection() {
  const { accessToken, userRole } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [currPasswordVisible, setCurrPasswordVisible] = useState(false);
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const { mutate: resetPassword, isLoading: resettingPassword } =
    useResetPassword();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(passwordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
  });

  const newPassword = watch("newPassword");

  const toggleView = () => {
    setIsUpdating((curr) => !curr);
  };

  const onResetPassword = ({ currentPassword, newPassword }) => {
    resetPassword(
      {
        previous_password: currentPassword,
        new_password: newPassword,
        access_token: accessToken,
      },
      {
        onSuccess: (data) => {
          toast.success(data.message);
          reset();
          toggleView();
        },
        onError: (error) => {
          console.log("[SETTINGS] Reset password:: ", error);
          toast.error("Was not possible to reset password. Please try again.");
        },
      },
    );
  };

  return (
    <section aria-labelledby="business-information-heading">
      <form onSubmit={handleSubmit(onResetPassword)}>
        <div className="shadow sm:overflow-hidden sm:rounded-md">
          <div className="bg-white px-4 py-6 sm:p-6">
            <div>
              <h2 className="text-lg font-medium leading-6 text-gray-900">
                Privacy & security
              </h2>
              <div className="text-sm text-gray-500 mt-1"></div>
            </div>

            <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
              {isUpdating ? (
                <>
                  <div className="pt-6 sm:flex">
                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                      Current password
                    </dt>
                    <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                      <Controller
                        control={control}
                        name="currentPassword"
                        render={({ field }) => (
                          <div className="flex flex-col w-full">
                            <div className="relative mt-2 w-full">
                              <InputBase
                                {...field}
                                type={currPasswordVisible ? "text" : "password"}
                              />
                              <div
                                className="absolute inset-x-0 bottom-0 border-t border-gray-300 peer-focus:border-t-2 peer-focus:border-black"
                                aria-hidden="true"
                              />
                              <div
                                className=" absolute hover:cursor-pointer transition-all inset-y-0 right-0 flex gap-x-2 items-center pr-3"
                                onClick={() =>
                                  setCurrPasswordVisible((prev) => !prev)
                                }
                              >
                                {currPasswordVisible ? (
                                  <EyeOff className="h-5 w-5" />
                                ) : (
                                  <Eye className="h-5 w-5" />
                                )}
                              </div>
                            </div>
                            {errors.currentPassword && (
                              <p
                                className="mt-2 text-sm text-red-600"
                                id="currentPassword-error"
                              >
                                {errors.currentPassword.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    </dd>
                  </div>
                  <div className="pt-6 sm:flex">
                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                      New password
                    </dt>
                    <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                      <Controller
                        control={control}
                        name="newPassword"
                        render={({ field }) => (
                          <div className="flex flex-col w-full gap-y-2">
                            <div className="relative mt-2 w-full">
                              <InputBase
                                {...field}
                                type={newPasswordVisible ? "text" : "password"}
                              />
                              <div
                                className="absolute inset-x-0 bottom-0 border-t border-gray-300 peer-focus:border-t-2 peer-focus:border-black"
                                aria-hidden="true"
                              />
                              <div
                                className=" absolute hover:cursor-pointer transition-all inset-y-0 right-0 flex gap-x-2 items-center pr-3"
                                onClick={() =>
                                  setNewPasswordVisible((prev) => !prev)
                                }
                              >
                                {newPasswordVisible ? (
                                  <EyeOff className="h-5 w-5" />
                                ) : (
                                  <Eye className="h-5 w-5" />
                                )}
                              </div>
                            </div>
                            {newPassword && (
                              <PasswordValidation password={newPassword} />
                            )}
                          </div>
                        )}
                      />
                    </dd>
                  </div>
                  <div className="pt-6 sm:flex">
                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                      Confirm new password
                    </dt>
                    <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                      <Controller
                        control={control}
                        name="confirmNewPassword"
                        render={({ field }) => (
                          <div className="w-full flex flex-col">
                            <div className="relative mt-2 w-full">
                              <InputBase
                                {...field}
                                type={
                                  confirmPasswordVisible ? "text" : "password"
                                }
                              />
                              <div
                                className="absolute inset-x-0 bottom-0 border-t border-gray-300 peer-focus:border-t-2 peer-focus:border-black"
                                aria-hidden="true"
                              />
                              <div
                                className=" absolute hover:cursor-pointer transition-all inset-y-0 right-0 flex gap-x-2 items-center pr-3"
                                onClick={() =>
                                  setConfirmPasswordVisible((prev) => !prev)
                                }
                              >
                                {confirmPasswordVisible ? (
                                  <EyeOff className="h-5 w-5" />
                                ) : (
                                  <Eye className="h-5 w-5" />
                                )}
                              </div>
                            </div>
                            {errors.confirmNewPassword && (
                              <p
                                className="mt-2 text-sm text-red-600"
                                id="confirmNewPassword-error"
                              >
                                {errors.confirmNewPassword.message}
                              </p>
                            )}
                          </div>
                        )}
                      />
                    </dd>
                  </div>
                </>
              ) : (
                <div className="pt-6 sm:flex">
                  <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
                    Password
                  </dt>
                  <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                    <div className="text-gray-900">************</div>
                    {userRole === "client" && (
                      <button
                        type="button"
                        className="font-semibold text-indigo-600 hover:text-indigo-500"
                        onClick={toggleView}
                      >
                        Update
                      </button>
                    )}
                  </dd>
                </div>
              )}
            </dl>
          </div>
          <div className="bg-white px-4 py-3 text-right sm:px-6">
            {isUpdating && (
              <div className="flex gap-x-3 justify-end">
                <Button
                  disabled={resettingPassword}
                  type="button"
                  variant="outline"
                  onClick={() => {
                    toggleView();
                    reset();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={resettingPassword}>
                  Save{" "}
                  {resettingPassword && (
                    <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </form>
    </section>
  );
}

const schema = yup
  .object({
    firstName: yup.string().required("First name is a required field"),
    lastName: yup.string().required("Last name is a required field"),
    email: yup
      .string()
      .email("Email must be a valid email")
      .required("Email is a required field"),
  })
  .required();

const CheckIcon = ({ size = 30, strokeWidth = 4.5 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    className="mx-1"
  >
    <defs>
      <linearGradient id="checkGradient" x1="0" y1="0" x2="1" y2="0">
        <stop offset="35%" stopColor="#58ae52" />
        <stop offset="35%" stopColor="#91ca8f" />
      </linearGradient>
    </defs>
    <path
      d="M20 6L9 17L4 12"
      stroke="url(#checkGradient)"
      strokeWidth={strokeWidth}
      strokeLinecap="square"
      strokeLinejoin="square"
    />
  </svg>
);

function AddAdminModal() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data: tenancy } = useTenancy();
  const { mutate: sendInvite, isLoading } = useAdminInvite();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const onSubmit = ({ firstName, lastName, email }) => {
    const payload = {
      email,
      first_name: firstName,
      last_name: lastName,
      subdomain: tenancy?.subdomain,
    };

    sendInvite(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries(["tenancy"]);
        reset();
        setOpen(false);
        toast.success(
          `Invitation to ${firstName} ${lastName} sent successfully!`,
        );
      },
      onError: (error) => {
        if (error.response.status === 409) {
          toast.error(
            error?.response?.data?.detail ||
              "The maximum number of admin users (10) has been reached.",
            {
              autoClose: 15000,
              hideProgressBar: false,
            },
          );
        } else {
          toast.error(
            "Something went wrong sending the invite. Please try again.",
            {
              autoClose: 10000,
              hideProgressBar: false,
            },
          );
        }
      },
    });
  };

  return (
    <Dialog onOpenChange={(value) => setOpen(value)} open={open}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          Add Admin
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="font-bold text-2xl">
            Empower Your Team with Admin Access
          </DialogTitle>
        </DialogHeader>
        <div>
          <p className="text-sm">
            Give your team the tools they need to help manage your business:
          </p>
          <ul className="list-none mt-2">
            <li className="flex items-center">
              <CheckIcon />
              <span className="text-sm font-medium text-gray-800">
                Seamlessly create, edit, and send invoices.
              </span>
            </li>
            <li className="flex items-center mt-2">
              <CheckIcon />
              <span className="text-sm font-medium text-gray-800">
                Efficiently manage and build your contact list.
              </span>
            </li>
            <li className="flex items-center mt-2">
              <CheckIcon />
              <span className="text-sm font-medium text-gray-800">
                Take care of invoice payments with ease.
              </span>
            </li>
          </ul>
          <div className="border-t border-gray-300 my-3"></div>
          <p className="mt-4 text-sm">
            While admins have robust capabilities, they cannot:
          </p>
          <ul className="list-none mt-2">
            <li className="flex items-center mt-2">
              <X
                size={38}
                color="#dedcd9"
                strokeWidth={3}
                strokeLinecap="butt"
              />

              <span className="text-sm font-medium text-gray-800">
                Link or remove bank accounts.
              </span>
            </li>
            <li className="flex items-center mt-2">
              <X
                size={38}
                color="#dedcd9"
                strokeWidth={3}
                strokeLinecap="butt"
              />
              <span className="text-sm font-medium text-gray-800">
                Initiate external transfers.
              </span>
            </li>
            <li className="flex items-center mt-2">
              <X
                size={38}
                color="#dedcd9"
                strokeWidth={3}
                strokeLinecap="butt"
              />
              <span className="text-sm font-medium text-gray-800">
                Change account settings or manage other admins.
              </span>
            </li>
          </ul>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle className="font-bold text-2xl">
              Send an invite to get started
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 w-9/12">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-y-2">
                <Input
                  {...register("firstName")}
                  id="firstName"
                  placeholder="First Name"
                />
                {errors.firstName && (
                  <div className="text-xs font-medium text-red-600">
                    {errors.firstName?.message}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-y-2">
                <Input
                  {...register("lastName")}
                  id="lastName"
                  placeholder="Last Name"
                />
                {errors.lastName && (
                  <div className="text-xs font-medium text-red-600">
                    {errors.lastName?.message}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-y-2">
              <Input
                {...register("email")}
                id="email"
                placeholder="Email Address"
              />
              {errors.email && (
                <div className="text-xs font-medium text-red-600">
                  {errors.email?.message}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                reset();
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              Send Invite
              {isLoading && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddAuthUserModal({ disabled = false }) {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { mutate: sendInvite, isLoading: inviting } = useAuthUserInvite();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const onSubmit = ({ firstName, lastName, email }) => {
    const payload = {
      email,
      first_name: firstName,
      last_name: lastName,
    };

    sendInvite(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries(["tenancy"]);
        reset();
        setOpen(false);
        toast.success(
          `Invite email to ${firstName} ${lastName} sent successfully!`,
          {
            autoClose: 10000,
            hideProgressBar: false,
          },
        );
      },
      onError: (error) => {
        if (error.response.status === 409) {
          toast.error(
            error?.response?.data?.detail ||
              "The maximum number of authorized users (4) has been reached.",
            {
              autoClose: 15000,
              hideProgressBar: false,
            },
          );
        } else {
          toast.error(
            "Something went wrong sending the invite. Please try again.",
            {
              autoClose: 10000,
              hideProgressBar: false,
            },
          );
        }
      },
    });
  };

  return (
    <Dialog onOpenChange={(value) => setOpen(value)} open={open}>
      <DialogTrigger asChild>
        <Button disabled={disabled}>
          <Plus className="w-4 h-4 mr-2" />
          Add Auth User
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Invite to Authorized User Access</DialogTitle>
          <div className="text-sm text-muted-foreground">
            Empower a trusted team member with top-level access to your account.
            An Authorized User can do everything an Admin can—plus manage
            high-level settings to keep your account running smoothly.{" "}
            <div className="mt-3 mb-1 font-medium text-base text-slate-900">
              Authorized users can:
            </div>
            <ul>
              <li className="flex items-center">
                <ArrowRight className="w-4 h-4 mr-2" />
                Add or Remove Bank Accounts
              </li>
              <li className="flex items-center">
                <ArrowRight className="w-4 h-4 mr-2" />
                Move Money
              </li>
              <li className="flex items-center">
                <ArrowRight className="w-4 h-4 mr-2" />
                Manage Platform Settings
              </li>
              <li className="flex items-center">
                <ArrowRight className="w-4 h-4 mr-2" />
                Oversee Admin Users
              </li>
              <li className="flex items-center">
                <ArrowRight className="w-4 h-4 mr-2" />
                Plus All Standard Admin Functions
              </li>
            </ul>
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-4 mb-4 mt-2 ">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-y-2">
                <Input
                  {...register("firstName")}
                  id="firstName"
                  placeholder="First Name"
                />
                {errors.firstName && (
                  <div className="text-xs font-medium text-red-600">
                    {errors.firstName?.message}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-y-2">
                <Input
                  {...register("lastName")}
                  id="lastName"
                  placeholder="Last Name"
                />
                {errors.lastName && (
                  <div className="text-xs font-medium text-red-600">
                    {errors.lastName?.message}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-y-2">
              <Input
                {...register("email")}
                id="email"
                placeholder="Email Address"
              />
              {errors.email && (
                <div className="text-xs font-medium text-red-600">
                  {errors.email?.message}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                reset();
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={inviting}>
              Send Invite
              {inviting && <Loader2 className="w-4 h-4 ml-2 animate-spin" />}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddAdminFormSection() {
  const queryClient = useQueryClient();
  const { data: tenancy } = useTenancy();
  const { mutate: sendInvite, isLoading } = useAdminInvite();
  const {
    control,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    formState: { errors, touchedFields },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const onSubmit = ({ firstName, lastName, email }) => {
    const payload = {
      email,
      first_name: firstName,
      last_name: lastName,
      subdomain: tenancy?.subdomain,
    };

    sendInvite(payload, {
      onSuccess: () => {
        reset();
        queryClient.invalidateQueries(["tenancy"]);
        toast.success(
          `Invitation to ${firstName} ${lastName} sent successfully!`,
        );
      },
      onError: (error) => {
        if (error.response.status === 409) {
          toast.error(
            error?.response?.data?.detail ||
              "The maximum number of admin users (10) has been reached.",
            {
              autoClose: 15000,
              hideProgressBar: false,
            },
          );
        } else {
          toast.error(
            "Something went wrong sending the invite. Please try again.",
            {
              autoClose: 10000,
              hideProgressBar: false,
            },
          );
        }
      },
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ProfileSection>
          <OptionLabel style={{ paddingBottom: "4px" }}>
            Add Account Administrator
          </OptionLabel>
          <div className="pb-8 font-inter font-normal text-base text-gray-500 max-w-[830px]">
            Admins have access to send invoices, create quotes, manage
            integrations and contacts but are not allowed to move money or pay
            bills. You can add up to 10 administrators to your platform.
          </div>

          <FormField>
            <FormLabel>First / Last Name</FormLabel>
            <Controller
              name="firstName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <StyledInput
                  {...field}
                  setValue={(value) => {
                    setValue("firstName", value);
                    clearErrors("firstName");
                  }}
                  width={"243.5px"}
                  mBottom={20}
                  mRight={24}
                  error={errors.firstName?.message}
                />
              )}
            />

            <Controller
              name="lastName"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <StyledInput
                  {...field}
                  setValue={(value) => {
                    setValue("lastName", value);
                    clearErrors("lastName");
                  }}
                  width={"243.5px"}
                  mBottom={20}
                />
              )}
            />
          </FormField>

          <FormField>
            <FormLabel>Email</FormLabel>
            <Controller
              name="email"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <StyledInput
                  {...field}
                  setValue={(value) => {
                    setValue("email", value);
                    clearErrors("email");
                  }}
                  width={512}
                  mBottom={20}
                />
              )}
            />
          </FormField>
          <div className="text-sm font-medium text-red-600">
            {errors.firstName?.message}
          </div>
          <div className="text-sm font-medium text-red-600">
            {errors.lastName?.message}
          </div>
          <div className="text-sm font-medium text-red-600">
            {errors.email?.message}
          </div>
          <BtnSection>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Send invite"
              )}
            </Button>
          </BtnSection>
        </ProfileSection>
      </form>
    </>
  );
}

function AdminTable() {
  const queryClient = useQueryClient();
  const { data } = usePlatformUsers({ userType: "admin" });
  const { mutate: deleteAdmin, isLoading: deleting } = useDeleteAdmin();

  const handleDelete = ({ email_address, user_id }) => {
    deleteAdmin(
      { email: email_address, userId: user_id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["tenancy"]);
          toast.success("Admin user deleted successfully!");
        },
        onError: () => {
          toast.error(
            "Something went wrong while deleting admin user. Please try again.",
          );
        },
      },
    );
  };

  return (
    <div className="max-w-[823px]">
      <div className="flow-root">
        <div className="-mx-8 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Name
                    </th>
                    <th
                      scope="col"
                      className="hidden lg:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Email
                    </th>
                    <th
                      scope="col"
                      className="hidden sm:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                    >
                      <span className="sr-only">Delete</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {data?.map((user) => {
                    if (user.user_type === "owner") return undefined;

                    return (
                      <tr key={user.email_address}>
                        <td className="whitespace-nowrap py-4 pl-8 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {`${user.first_name} ${user.last_name}`}
                          <dl className="font-normal lg:hidden">
                            <dt className="sr-only lg:hidden">Email</dt>
                            <dd className="mt-1 truncate text-gray-500 lg:hidden">
                              {user.email_address}
                            </dd>
                            <dt className="sr-only sm:hidden">Status</dt>
                            <dd className="mt-1 truncate text-gray-500 sm:hidden">
                              {user.record_status}
                            </dd>
                          </dl>
                        </td>

                        <td className="hidden lg:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {user.email_address}
                        </td>
                        <td className="hidden sm:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {user.record_status}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          {user.user_type === "admin" && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="sm"
                                >
                                  Delete
                                  <span className="sr-only">, {user.name}</span>
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    Are you absolutely sure?
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {`Are you sure you want to delete ${user.first_name} ${user.last_name}? This action cannot be undone. If you change your mind you will have to send an invite again.`}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel disabled={deleting}>
                                    Cancel
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    disabled={deleting}
                                    onClick={() => handleDelete(user)}
                                    className="bg-red-600 hover:bg-red-700 disabled:bg-red-100"
                                  >
                                    Continue
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
